import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, FileUp, History, Loader2, RotateCcw, Upload } from "lucide-react";
import api, { errMsg } from "@/lib/api";

const CANON_LABELS = {
  handle: "Handle (grouping key)", title: "Title", body_html: "Body (HTML)", vendor: "Vendor",
  type: "Type / Category", tags: "Tags", published: "Published", status: "Status",
  option1_name: "Option1 Name", option1_value: "Option1 Value",
  option2_name: "Option2 Name", option2_value: "Option2 Value",
  option3_name: "Option3 Name", option3_value: "Option3 Value",
  variant_sku: "Variant SKU", variant_grams: "Variant Grams (shipping only)",
  variant_inventory: "Variant Inventory Qty", variant_price: "Variant Price (legacy only)",
  variant_compare_at: "Variant Compare At Price", variant_image: "Variant Image",
  image_src: "Image Src", image_position: "Image Position", image_alt: "Image Alt Text",
  seo_title: "SEO Title", seo_description: "SEO Description",
  metal_weight: "metal_weight metafield", stone_carats: "stone_carats metafield",
  stone_rate: "stone_prices_per_carat metafield", stone_types: "stone_types metafield",
  sizes: "Sizes metafield", making_charges: "making_charges metafield",
  markup_percentage: "markup_percentage", laser_cost: "laser_cost", packaging_cost: "packaging_cost",
  show_breakdown: "show_breakdown metafield", legacy_gold_rate: "Gold rate (legacy ref)",
  legacy_silver_rate: "Silver Rate (legacy ref)", attr_material: "Jewelry material",
  attr_jewelry_type: "Jewelry type", attr_ring_design: "Ring design",
  attr_gender: "Target gender", attr_color: "Color", style_description: "Style Description",
};

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function ImportExport() {
  const [file, setFile] = useState(null);
  const [data, setData] = useState(null);
  const [mapping, setMapping] = useState(null);
  const [presets, setPresets] = useState([]);
  const [dryRun, setDryRun] = useState(true);
  const [updateOnly, setUpdateOnly] = useState(false);
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);
  const [history, setHistory] = useState([]);
  const pollRef = useRef(null);

  const loadHistory = useCallback(() => {
    api.get("/admin/import/history").then((r) => setHistory(r.data)).catch(() => {});
    api.get("/admin/import/presets").then((r) => setPresets(r.data)).catch(() => {});
  }, []);
  useEffect(loadHistory, [loadHistory]);
  useEffect(() => () => clearInterval(pollRef.current), []);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/import/upload", fd);
      setData(r.data);
      if (r.data.format === "shopify") setMapping({ columns: r.data.detected, category_map: {}, axis_map: {}, default_category: "", default_product_type: "jewellery" });
      toast.success(`Parsed ${r.data.row_count} rows — ${r.data.format} format detected`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const revalidate = async (m = mapping, uo = updateOnly) => {
    setBusy(true);
    try {
      const r = await api.post("/admin/import/preview", { file_token: data.file_token, mapping: m, update_only: uo });
      setData({ ...data, ...r.data });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const execute = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/import/execute", {
        file_token: data.file_token, mapping, dry_run: dryRun, update_only: updateOnly,
      });
      const jobId = r.data.job_id;
      toast.message(dryRun ? "Dry run started" : "Import started", { description: `Batch ${r.data.batch_id}` });
      pollRef.current = setInterval(async () => {
        const jr = await api.get(`/admin/import/jobs/${jobId}`);
        setJob(jr.data);
        if (["complete", "failed"].includes(jr.data.status)) {
          clearInterval(pollRef.current);
          loadHistory();
          if (jr.data.status === "complete") toast.success(`Import ${dryRun ? "dry-run " : ""}complete`);
          else toast.error("Import failed — see report");
        }
      }, 1500);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const rollback = async (batchId) => {
    if (!window.confirm(`Roll back import batch ${batchId}? Products it created will be deleted and products it updated will be restored.`)) return;
    try {
      const r = await api.post(`/admin/import/rollback/${batchId}`);
      toast.success(`Rolled back: ${r.data.created_deleted} created deleted, ${r.data.updated_restored} restored`);
      loadHistory();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const download = async (url, name) => {
    const r = await api.get(url, { responseType: "blob" });
    const note = r.headers["x-export-note"];
    if (note) toast.warning(note, { duration: 8000 });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = name;
    a.click();
  };

  const savePreset = async () => {
    const name = window.prompt("Preset name:");
    if (!name) return;
    await api.post("/admin/import/presets", { name, mapping });
    toast.success(`Preset "${name}" saved`);
    loadHistory();
  };

  const counts = data?.counts;
  const jobDone = job && ["complete", "failed"].includes(job.status);

  return (
    <div className="space-y-6" data-testid="import-export-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Bulk Import / Export</h1>
          <p className="text-sm text-neutral-500">Shopify CSV, Siyaara template, or inventory file. Imports are always drafts — nothing publishes from here.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => download("/admin/import/template", "siyaara-products-template.csv")} className="px-3 py-2 text-xs border rounded hover:bg-neutral-50" data-testid="download-template-btn"><Download size={13} className="inline mr-1" />Template</button>
          <button onClick={() => download("/admin/import/export/native", "siyaara-catalogue.csv")} className="px-3 py-2 text-xs border rounded hover:bg-neutral-50" data-testid="export-native-btn">Export catalogue</button>
          <button onClick={() => download("/admin/import/export/shopify", "shopify-export.csv")} className="px-3 py-2 text-xs border rounded hover:bg-neutral-50" data-testid="export-shopify-btn">Export Shopify format</button>
        </div>
      </div>

      {/* upload */}
      <section className="bg-white border rounded p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 px-4 py-2.5 border-2 border-dashed rounded cursor-pointer hover:border-brand text-sm" data-testid="csv-drop">
            <FileUp size={16} /> {file ? file.name : "Choose Shopify export / template / inventory CSV"}
            <input type="file" accept=".csv" className="hidden" data-testid="csv-file-input" onChange={(e) => { setFile(e.target.files[0]); setData(null); setJob(null); }} />
          </label>
          <button onClick={upload} disabled={!file || busy} className="px-4 py-2 bg-neutral-900 text-white text-sm rounded disabled:opacity-40" data-testid="parse-btn">
            {busy ? <Loader2 size={14} className="animate-spin inline" /> : <Upload size={14} className="inline mr-1" />} Parse & validate
          </button>
        </div>
        {data && (
          <div className="flex flex-wrap gap-2 text-xs" data-testid="parse-summary">
            <span className="px-2 py-1 bg-neutral-100 rounded">{data.format} · {data.row_count} rows</span>
            <span className="px-2 py-1 bg-green-100 text-green-800 rounded">{counts?.products ?? data.inventory?.rows} products</span>
            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded">{counts?.variants} variants</span>
            <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded">{counts?.images} images</span>
            <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded">{counts?.warnings} warnings</span>
            <span className="px-2 py-1 bg-red-100 text-red-800 rounded">{counts?.errors} rejected products</span>
          </div>
        )}
      </section>

      {/* mapping (shopify only) */}
      {data?.format === "shopify" && mapping && (
        <section className="bg-white border rounded p-4 space-y-4" data-testid="mapping-step">
          <div className="flex items-center justify-between">
            <h2 className="font-medium text-sm">Column mapping — confirm or adjust</h2>
            <div className="flex gap-2 items-center">
              <select className="border rounded px-2 py-1.5 text-xs" data-testid="preset-select" onChange={(e) => {
                const p = presets.find((x) => x.name === e.target.value);
                if (p) { setMapping(p.mapping); revalidate(p.mapping); }
              }} defaultValue="">
                <option value="">Load preset…</option>
                {presets.map((p) => <option key={p.name} value={p.name}>{p.name}</option>)}
              </select>
              <button onClick={savePreset} className="px-3 py-1.5 text-xs border rounded" data-testid="save-preset-btn">Save as preset</button>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-x-6 gap-y-1.5 max-h-72 overflow-y-auto text-xs">
            {Object.entries(CANON_LABELS).map(([canon, label]) => (
              <label key={canon} className="flex items-center justify-between gap-2">
                <span className="text-neutral-600 truncate">{label}</span>
                <select className="border rounded px-1.5 py-1 w-44" value={mapping.columns[canon] || ""} data-testid={`map-${canon}`}
                  onChange={(e) => setMapping({ ...mapping, columns: { ...mapping.columns, [canon]: e.target.value } })}>
                  <option value="">— ignore —</option>
                  {data.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
              </label>
            ))}
          </div>
          {data.type_values?.length > 0 && (
            <div>
              <h3 className="text-xs font-medium text-neutral-500 mb-1.5">Type → our category</h3>
              <div className="flex flex-wrap gap-3 text-xs">
                {data.type_values.map((t) => (
                  <label key={t} className="flex items-center gap-1.5">
                    <span className="px-2 py-1 bg-neutral-100 rounded">{t}</span>→
                    <input className="border rounded px-2 py-1 w-28" placeholder="e.g. Rings" defaultValue={/ring/i.test(t) ? "Rings" : ""}
                      data-testid={`catmap-${t}`}
                      onChange={(e) => setMapping({ ...mapping, category_map: { ...mapping.category_map, [t]: { category: e.target.value } } })} />
                  </label>
                ))}
              </div>
            </div>
          )}
          <button onClick={() => revalidate()} disabled={busy} className="px-4 py-2 bg-neutral-900 text-white text-xs rounded" data-testid="revalidate-btn">Re-run validation</button>
        </section>
      )}

      {/* inventory preview */}
      {data?.format === "inventory" && (
        <section className="bg-white border rounded p-4 text-sm" data-testid="inventory-preview">
          <p><b>{data.inventory.rows}</b> inventory rows — <b className="text-green-700">{data.inventory.matched} matched</b> by SKU, <b className="text-red-700">{data.inventory.unmatched} unmatched</b>. Stock updates map “On hand (current)” → stock.</p>
        </section>
      )}

      {/* validation preview */}
      {data && data.format !== "inventory" && (
        <section className="bg-white border rounded p-4 space-y-3" data-testid="validation-preview">
          <div className="flex flex-wrap items-center gap-4">
            <h2 className="font-medium text-sm">Validation preview — nothing is written yet</h2>
            <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} data-testid="dry-run-toggle" /> Dry run</label>
            <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={updateOnly} onChange={(e) => { setUpdateOnly(e.target.checked); revalidate(mapping, e.target.checked); }} data-testid="update-only-toggle" /> Update only (never create)</label>
          </div>
          {data.rejects?.length > 0 && (
            <div className="border border-red-200 rounded">
              <div className="px-3 py-2 bg-red-50 text-red-800 text-xs font-medium">Rejected — {data.rejects.length} products (fix source data and re-import; re-imports match on SKU)</div>
              <div className="max-h-44 overflow-y-auto text-xs divide-y" data-testid="rejects-table">
                {data.rejects.map((r) => (
                  <div key={r.handle} className="px-3 py-2">
                    <span className="font-mono text-neutral-500">{r.handle}</span> <b>{r.title}</b>
                    <ul className="ml-4 list-disc text-red-700">{r.gates.map((g) => <li key={g}>{g}</li>)}</ul>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="border rounded overflow-x-auto">
            <table className="w-full text-xs" data-testid="preview-table">
              <thead className="bg-neutral-50 text-neutral-500">
                <tr>{["Product", "Action", "Variants", "Images", "Purity", "Legacy price", "Computed today", "Flags"].map((h) => <th key={h} className="text-left px-3 py-2 font-medium">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y">
                {(data.products || []).map((p) => (
                  <tr key={p.slug} className={p.action === "rejected" ? "bg-red-50/50" : ""}>
                    <td className="px-3 py-2"><b>{p.title}</b><div className="text-neutral-400 font-mono">{p.slug}</div></td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded ${p.action === "create" ? "bg-green-100 text-green-800" : p.action === "update" ? "bg-blue-100 text-blue-800" : "bg-red-100 text-red-800"}`} data-testid={`action-${p.slug}`}>
                        will {p.action}
                      </span>
                      {p.reason && <div className="text-red-600 mt-0.5">{p.reason}</div>}
                    </td>
                    <td className="px-3 py-2">{p.variants}</td>
                    <td className="px-3 py-2">{p.images}</td>
                    <td className="px-3 py-2">{p.purity || "—"}</td>
                    <td className="px-3 py-2">{p.legacy_price_max ? inr(p.legacy_price_max) : "—"}</td>
                    <td className="px-3 py-2">{inr(p.computed_min)}{p.computed_max !== p.computed_min && `–${inr(p.computed_max)}`}</td>
                    <td className="px-3 py-2 space-x-1">
                      {p.legacy_below_metal && <span className="px-1.5 py-0.5 bg-red-600 text-white rounded" title="Legacy price is below metal value">BELOW METAL</span>}
                      {p.awaiting_making_charge && <span className="px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded">needs making charge</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button onClick={execute} disabled={busy || (job && !jobDone)} className="px-5 py-2.5 bg-brand text-white text-sm rounded font-medium disabled:opacity-40" data-testid="run-import-btn">
            {job && !jobDone ? `Importing… ${job.processed || 0}/${job.total || 0}` : dryRun ? "Run dry run" : "Import (drafts only)"}
          </button>
        </section>
      )}

      {/* inventory execute */}
      {data?.format === "inventory" && (
        <button onClick={execute} disabled={busy} className="px-5 py-2.5 bg-brand text-white text-sm rounded font-medium" data-testid="run-inventory-btn">
          {job && !jobDone ? `Updating… ${job.processed || 0}/${job.total || 0}` : "Apply stock updates"}
        </button>
      )}

      {/* job result */}
      {jobDone && (
        <section className="bg-white border rounded p-4 space-y-2" data-testid="job-result">
          <h2 className="font-medium text-sm">Import report — batch <span className="font-mono">{job.batch_id}</span> {job.dry_run && <span className="text-amber-600">(dry run)</span>}</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="px-2 py-1 bg-green-100 rounded">{job.created || 0} created</span>
            <span className="px-2 py-1 bg-blue-100 rounded">{job.updated || 0} updated</span>
            <span className="px-2 py-1 bg-red-100 rounded">{(job.rejected || 0) + (job.gate_rejects?.length || 0)} rejected</span>
            <span className="px-2 py-1 bg-neutral-100 rounded">{job.images_fetched || 0} images</span>
            <span className="px-2 py-1 bg-red-100 text-red-800 rounded">{job.flagged_below_metal || 0} priced below metal</span>
            <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded">{job.awaiting_making || 0} awaiting making charge</span>
          </div>
          <div className="flex gap-2">
            <button onClick={() => download(`/admin/import/jobs/${job.id}/report`, `import-report-${job.id}.csv`)} className="px-3 py-2 text-xs border rounded" data-testid="download-report-btn"><Download size={12} className="inline mr-1" />Download row report</button>
            {!job.dry_run && <a href="/admin/needs-pricing" className="px-3 py-2 text-xs bg-brand text-white rounded" data-testid="goto-pricing-queue">Open Needs Pricing queue →</a>}
          </div>
        </section>
      )}

      {/* history + rollback */}
      <section className="bg-white border rounded p-4" data-testid="import-history">
        <h2 className="font-medium text-sm mb-2 flex items-center gap-2"><History size={14} /> Import history</h2>
        <table className="w-full text-xs">
          <thead className="text-neutral-500"><tr>{["When", "File", "By", "Created", "Updated", "Rejected", "", ""].map((h) => <th key={h} className="text-left py-1.5 pr-3 font-medium">{h}</th>)}</tr></thead>
          <tbody className="divide-y">
            {history.map((e, i) => (
              <tr key={e.batch_id}>
                <td className="py-1.5 pr-3">{new Date(e.at).toLocaleString("en-IN")}</td>
                <td className="pr-3">{e.filename}{e.dry_run && <span className="text-amber-600 ml-1">(dry run)</span>}</td>
                <td className="pr-3">{e.by}</td>
                <td className="pr-3 text-green-700">{e.products_created}</td>
                <td className="pr-3 text-blue-700">{e.products_updated}</td>
                <td className="pr-3 text-red-700">{e.products_rejected}</td>
                <td className="pr-3">{e.rolled_back && <span className="text-neutral-400">rolled back</span>}</td>
                <td>{i === 0 && !e.dry_run && !e.rolled_back && (
                  <button onClick={() => rollback(e.batch_id)} className="text-red-600 hover:underline flex items-center gap-1" data-testid="rollback-btn"><RotateCcw size={11} /> Roll back</button>)}
                </td>
              </tr>
            ))}
            {!history.length && <tr><td colSpan={8} className="py-3 text-neutral-400">No imports yet.</td></tr>}
          </tbody>
        </table>
      </section>
    </div>
  );
}
